// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { onAuthChange, logoutUser } from '../services/firebaseAuth';
import api from '../services/api';
import API from '../components/config';
import { toast } from 'react-toastify';

export const AuthContext = createContext();

// Development mode - DISABLED to use Firebase authentication
const DEV_MODE = false; // Changed from: process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BYPASS === 'true'

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage if available, or use dev mode
  const [authData, setAuthData] = useState(() => {
    // If in dev bypass mode, return mock auth data
    if (DEV_MODE) {
      return {
        access_token: 'dev_token_123',
        user_id: 'dev_user_123',
        isFirstLogin: localStorage.getItem('dev_first_login') !== 'false',
        username: 'DevUser',
        email: 'dev@example.com'
      };
    }
    return undefined; // undefined = loading, null = not authenticated
  });
  
  // Subscribe to Firebase auth state changes
  useEffect(() => {
    if (DEV_MODE) {
      return;
    }
    
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        // User is signed in
        const token = await user.getIdToken();
        
        // Check if user account is blocked before setting authData - MUST complete before setting authData
        let isBlocked = false;
        try {
          const response = await api.get(`${API}/api/firebase-auth/check-blocked`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Explicitly check the response
          if (response.data?.blocked === true) {
            isBlocked = true;
          }
        } catch (blockedError) {
          console.error('Blocked check error in AuthContext:', blockedError);
          // Check if account is blocked
          if (blockedError.response?.status === 403) {
            isBlocked = true;
            const errorMessage = blockedError.response?.data?.error || 'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
            console.error('❌ Account is blocked (AuthContext):', errorMessage);
            // Don't show toast here - Login.js will handle it to avoid duplicates
            // Log out the user immediately
            await logoutUser();
            setAuthData(null);
            return; // Don't set authData, user is blocked
          }
          // Check response data for blocked status
          if (blockedError.response?.data?.blocked === true || 
              (blockedError.response?.data?.error && blockedError.response.data.error.toLowerCase().includes('blocked'))) {
            isBlocked = true;
            const errorMessage = blockedError.response.data.error || 'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
            console.error('❌ Account is blocked (AuthContext, from response data):', errorMessage);
            // Don't show toast here - Login.js will handle it to avoid duplicates
            await logoutUser();
            setAuthData(null);
            return;
          }
          // If it's a different error, continue (might be network issue)
        }
        
        // If blocked, don't set authData
        if (isBlocked) {
          const errorMessage = 'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
          console.error('❌ Account is blocked (AuthContext, isBlocked flag):', errorMessage);
          // Don't show toast here - Login.js will handle it to avoid duplicates
          await logoutUser();
          setAuthData(null);
          return;
        }
        
        // Tutorial completion is now tracked in database, not localStorage
        // We'll check it from the user profile when it's fetched
        setAuthData({
          access_token: token,
          user_id: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          isFirstLogin: undefined,  // Will be set from user profile
          tutorialCompletedLocally: false
        });
      } else {
        // User is signed out
        setAuthData(null);
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  // Helper function to mark tutorial as completed
  const markTutorialCompleted = async () => {
    if (DEV_MODE) {
      localStorage.setItem('dev_first_login', 'false');
      setAuthData(prev => ({ ...prev, isFirstLogin: false }));
      return;
    }
    
    if (authData?.user_id) {
      try {
        // Call backend to mark tutorial as completed in database
        await api.post(`${API}/api/dashboard/mark-tutorial-completed`);
        
        // Update local state immediately for instant UX
        setAuthData(prev => ({ 
          ...prev, 
          isFirstLogin: false,
          tutorialCompletedLocally: true 
        }));
      } catch (error) {
        console.error('Error marking tutorial as completed:', error);
        // Still update local state even if API call fails
        setAuthData(prev => ({ 
          ...prev, 
          isFirstLogin: false,
          tutorialCompletedLocally: true 
        }));
      }
    }
  };

  // Helper function to reset tutorial (for dev testing)
  const resetTutorial = () => {
    if (DEV_MODE) {
      localStorage.removeItem('dev_first_login');
      setAuthData(prev => ({ ...prev, isFirstLogin: true }));
    } else if (authData?.user_id) {
      // In production, reset tutorial for this specific user
      localStorage.removeItem(`tutorial_completed_${authData.user_id}`);
      setAuthData(prev => ({ ...prev, isFirstLogin: true }));
    }
  };

  return (
    <AuthContext.Provider value={{ 
      authData, 
      setAuthData, 
      markTutorialCompleted, 
      resetTutorial
    }}>
      {children}
    </AuthContext.Provider>
  );
};
